// src/services/firebaseStorage.js - SERVICIO COMPLETO actualizado
import { initializeApp, getApps, getApp } from 'firebase/app';
import { 
  getStorage, 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject,
  listAll,
  getMetadata 
} from 'firebase/storage';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  signOut,
  onAuthStateChanged 
} from 'firebase/auth';
// Configuración de Firebase (debe estar en variables de entorno)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Verificar si Firebase ya está inicializado
const app = getApps().length === 0 ? 
  initializeApp(firebaseConfig) : getApp();

const storage = getStorage(app);
const auth = getAuth(app);

console.log('app firebase: ', app);

// Función para autenticar al usuario
const autenticarUsuario = async () => {
  try {
    // Verificar si ya está autenticado
    const usuarioActual = auth.currentUser;
    if (usuarioActual) {
      console.log('Firebase Auth - Usuario ya autenticado:', usuarioActual.email);
      return usuarioActual;
    }

    // Obtener credenciales del entorno
    const email = process.env.REACT_APP_FIREBASE_AUTH_EMAIL;
    const password = process.env.REACT_APP_FIREBASE_AUTH_PASSWORD;

    if (!email || !password) {
      throw new Error('Credenciales de Firebase no configuradas en variables de entorno');
    }

    console.log('Firebase Auth - Autenticando usuario:', email);
    
    // Autenticar al usuario
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    console.log('Firebase Auth - Autenticación exitosa:', userCredential.user.email);
    return userCredential.user;
  } catch (error) {
    console.error('Firebase Auth - Error en autenticación:', error);
    
    let errorMessage = 'Error de autenticación';
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = 'Email inválido';
        break;
      case 'auth/user-disabled':
        errorMessage = 'Usuario deshabilitado';
        break;
      case 'auth/user-not-found':
        errorMessage = 'Usuario no encontrado';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Contraseña incorrecta';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Error de red';
        break;
      default:
        errorMessage = error.message || 'Error desconocido en autenticación';
    }
    
    throw new Error(errorMessage);
  }
};

// Función wrapper para operaciones que requieren autenticación
const ejecutarConAutenticacion = async (operacion) => {
  try {
    // Autenticar al usuario
    await autenticarUsuario();
    
    // Ejecutar la operación
    return await operacion();
  } catch (error) {
    console.error('Error en operación con autenticación:', error);
    throw error;
  }
};


const firebaseStorageService = {
  /**
   * Sube un archivo a Firebase Storage
   * @param {File} file - Archivo a subir
   * @param {string} path - Ruta donde subir el archivo
   * @param {function} onProgress - Callback para progreso de subida
   * @returns {Promise<Object>} - URL de descarga y metadata
   */
  subirArchivo: (file, path, onProgress = null) => {
    return ejecutarConAutenticacion(() => {
      return new Promise((resolve, reject) => {
        // Validaciones del archivo (tu código existente)
        if (!file) {
          reject(new Error('No se proporcionó archivo'));
          return;
        }

        const maxSize = 100 * 1024 * 1024;
        if (file.size > maxSize) {
          reject(new Error('El archivo excede el tamaño máximo de 100MB'));
          return;
        }

        const allowedTypes = ['application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          reject(new Error('Solo se permiten archivos PDF'));
          return;
        }

        const storageRef = ref(storage, path);
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(progress, snapshot.state);
            }
          },
          (error) => {
            console.error('Firebase Storage - Error subiendo archivo:', error);
            // Manejo de errores (tu código existente)
            let errorMessage = 'Error desconocido';
            switch (error.code) {
              case 'storage/unauthorized':
              errorMessage = 'No tienes permisos para subir este archivo';
              break;
              case 'storage/canceled':
                errorMessage = 'La subida fue cancelada';
                break;
              case 'storage/unknown':
                errorMessage = 'Error desconocido, intenta de nuevo';
                break;
              case 'storage/object-not-found':
                errorMessage = 'Archivo no encontrado';
                break;
              case 'storage/bucket-not-found':
                errorMessage = 'Bucket de almacenamiento no encontrado';
                break;
              case 'storage/project-not-found':
                errorMessage = 'Proyecto no encontrado';
                break;
              case 'storage/quota-exceeded':
                errorMessage = 'Cuota de almacenamiento excedida';
                break;
              case 'storage/unauthenticated':
                errorMessage = 'Usuario no autenticado';
                break;
              case 'storage/retry-limit-exceeded':
                errorMessage = 'Límite de reintentos excedido';
                break;
              case 'storage/invalid-format':
                errorMessage = 'Formato de archivo no válido';
                break;
              case 'storage/no-default-bucket':
                errorMessage = 'No se ha configurado un bucket por defecto';
                break;
              default:
                errorMessage = error.message || 'Error subiendo archivo';
            }
            reject(new Error(errorMessage));
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              const metadata = await getMetadata(uploadTask.snapshot.ref);
              
              console.log('Firebase Storage - Archivo subido exitosamente:', {
                path,
                downloadURL,
                size: metadata.size
              });
              
              resolve({
                downloadURL,
                fullPath: uploadTask.snapshot.ref.fullPath,
                name: uploadTask.snapshot.ref.name,
                size: metadata.size,
                contentType: metadata.contentType,
                timeCreated: metadata.timeCreated,
                updated: metadata.updated
              });
            } catch (error) {
              console.error('Firebase Storage - Error obteniendo información del archivo:', error);
              reject(new Error('Error obteniendo información del archivo subido'));
            }
          }
        );
      });
    });
  },

  /**
   * Genera una ruta única para el archivo
   * @param {number} entidadId - ID de la entidad
   * @param {number} tipoDocumentoId - ID del tipo de documento
   * @param {string} nombreArchivo - Nombre original del archivo
   * @returns {string} - Ruta única para el archivo
   */
  generarRutaArchivo: (entidadId, tipoDocumentoId, nombreArchivo) => {
    const timestamp = Date.now();
    const extension = nombreArchivo.split('.').pop();
    const nombreLimpio = nombreArchivo
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9._-]/g, '')
      .substring(0, 50);
    
    const rutaGenerada = `documentos/${entidadId}/${tipoDocumentoId}/${timestamp}_${nombreLimpio}`;
    console.log('Firebase Storage - Ruta generada:', rutaGenerada);
    return rutaGenerada;
  },

  /**
   * Elimina un archivo de Firebase Storage
   * @param {string} fullPath - Ruta completa del archivo
   * @returns {Promise<boolean>}
   */
  eliminarArchivo: async (rutaArchivo) => {
    try {
      console.log('Eliminando archivo de Firebase:', rutaArchivo);
      const storage = getStorage();
      const fileRef = ref(storage, rutaArchivo);
      await deleteObject(fileRef);
      console.log('✅ Archivo eliminado exitosamente de Firebase:', rutaArchivo);
      return true;
    } catch (error) {
      console.error('❌ Error eliminando archivo de Firebase:', error);
      // SI EL ARCHIVO NO EXISTE, NO ES UN ERROR CRÍTICO - CONTINUAR
      if (error.code === 'storage/object-not-found') {
        console.warn('El archivo ya no existe en Firebase:', rutaArchivo);
        return true; // ← IMPORTANTE: Retornar true, NO hacer throw
      }
      throw error; // Solo hacer throw para otros tipos de error
    }
  },

  /**
   * Obtiene la URL de descarga de un archivo
   * @param {string} fullPath - Ruta completa del archivo
   * @returns {Promise<string>} - URL de descarga
   */
  obtenerUrlDescarga: async (fullPath) => {
    return ejecutarConAutenticacion(async () => {
      try {
        console.log('Firebase Storage - Obteniendo URL de descarga:', fullPath);
        const fileRef = ref(storage, fullPath);
        const url = await getDownloadURL(fileRef);
        console.log('Firebase Storage - URL obtenida exitosamente');
        return url;
      } catch (error) {
        console.error('Firebase Storage - Error obteniendo URL de descarga:', error);
        if (error.code === 'storage/object-not-found') {
          throw new Error('El archivo no existe');
        } else {
          throw new Error('No se pudo obtener la URL de descarga');
        }
      }
    });
  },

  /**
   * Lista archivos de una entidad específica
   * @param {number} entidadId - ID de la entidad
   * @returns {Promise<Array>} - Lista de archivos
   */
  listarArchivosEntidad: async (entidadId) => {
    return ejecutarConAutenticacion(async () => {
      try {
        console.log('Firebase Storage - Listando archivos para entidad:', entidadId);
        const listRef = ref(storage, `documentos/${entidadId}/`);
        const res = await listAll(listRef);
        
        const archivos = await Promise.all(
          res.items.map(async (itemRef) => {
            try {
              const metadata = await getMetadata(itemRef);
              const downloadURL = await getDownloadURL(itemRef);
              
              return {
                name: itemRef.name,
                fullPath: itemRef.fullPath,
                downloadURL,
                size: metadata.size,
                contentType: metadata.contentType,
                timeCreated: metadata.timeCreated,
                updated: metadata.updated
              };
            } catch (error) {
              console.error('Firebase Storage - Error procesando archivo:', itemRef.name, error);
              return null;
            }
          })
        );

        const archivosValidos = archivos.filter(archivo => archivo !== null);
        console.log('Firebase Storage - Archivos listados:', archivosValidos.length);
        return archivosValidos;
      } catch (error) {
        console.error('Firebase Storage - Error listando archivos:', error);
        throw new Error('No se pudieron listar los archivos');
      }
    });
  },

  /**
   * Valida un archivo antes de subirlo
   * @param {File} file - Archivo a validar
   * @param {Object} restricciones - Restricciones del tipo de documento
   * @returns {Object} - Resultado de la validación
   */
  validarArchivo: (file, restricciones = {}) => {
    const errores = [];

    // Validar que existe el archivo
    if (!file) {
      errores.push('No se ha seleccionado ningún archivo');
      return { valido: false, errores };
    }

    // Validar tamaño
    const tamanoMaximo = restricciones.tamanoMaximo || 100 * 1024 * 1024; // 100MB por defecto
    if (file.size > tamanoMaximo) {
      const tamanoMB = Math.round(tamanoMaximo / (1024 * 1024));
      errores.push(`El archivo excede el tamaño máximo de ${tamanoMB}MB`);
    }

    // Validar tamaño mínimo (evitar archivos vacíos)
    if (file.size === 0) {
      errores.push('El archivo está vacío');
    }

    // Validar extensiones permitidas
    const extensionesPermitidas = restricciones.extensionesPermitidas || '.pdf';
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    const extensionesArray = extensionesPermitidas.split(',').map(ext => ext.trim().toLowerCase());
    
    if (!extensionesArray.includes(extension)) {
      errores.push(`Solo se permiten archivos con extensiones: ${extensionesPermitidas}`);
    }

    // Validar tipo MIME
    const tiposPermitidos = ['application/pdf'];
    if (!tiposPermitidos.includes(file.type)) {
      errores.push('El tipo de archivo no está permitido. Solo se permiten archivos PDF');
    }

    // Validar nombre de archivo
    if (file.name.length > 100) {
      errores.push('El nombre del archivo es demasiado largo (máximo 100 caracteres)');
    }

    if (file.name.length < 5) {
      errores.push('El nombre del archivo es demasiado corto (mínimo 5 caracteres)');
    }

    // Validar caracteres especiales en el nombre
    const caracteresProhibidos = /[<>:"/\\|?*\x00-\x1f]/;
    if (caracteresProhibidos.test(file.name)) {
      errores.push('El nombre del archivo contiene caracteres no permitidos');
    }

    // Validar que el archivo tenga extensión
    if (!file.name.includes('.')) {
      errores.push('El archivo debe tener una extensión válida');
    }

    console.log('Firebase Storage - Validación de archivo:', {
      archivo: file.name,
      tamaño: file.size,
      tipo: file.type,
      valido: errores.length === 0,
      errores
    });

    return {
      valido: errores.length === 0,
      errores,
      archivo: {
        nombre: file.name,
        tamaño: file.size,
        tipo: file.type,
        extension
      }
    };
  },

  /**
   * Formatea el tamaño de archivo para mostrar
   * @param {number} bytes - Tamaño en bytes
   * @returns {string} - Tamaño formateado
   */
  formatearTamaño: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes || isNaN(bytes)) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  /**
   * Verifica si Firebase está configurado correctamente
   * @returns {boolean} - True si está configurado
   */
  verificarConfiguracion: () => {
    try {
      const configurado = !!storage && !!app;
      console.log('Firebase Storage - Estado de configuración:', configurado);
      return configurado;
    } catch (error) {
      console.error('Firebase Storage - Error verificando configuración:', error);
      return false;
    }
  },

  /**
   * Obtiene información de un archivo sin descargarlo
   * @param {string} fullPath - Ruta completa del archivo
   * @returns {Promise<Object>} - Metadata del archivo
   */
  obtenerMetadataArchivo: async (fullPath) => {
    return ejecutarConAutenticacion(async () => {
      try {
        console.log('Firebase Storage - Obteniendo metadata:', fullPath);
        const fileRef = ref(storage, fullPath);
        const metadata = await getMetadata(fileRef);
        
        return {
          nombre: metadata.name,
          rutaCompleta: metadata.fullPath,
          tamaño: metadata.size,
          tipoContenido: metadata.contentType,
          fechaCreacion: metadata.timeCreated,
          fechaActualizacion: metadata.updated,
          bucket: metadata.bucket
        };
      } catch (error) {
        console.error('Firebase Storage - Error obteniendo metadata:', error);
        throw new Error('No se pudo obtener la información del archivo');
      }
    });
  },

  /**
   * Verifica si un archivo existe en Firebase Storage
   * @param {string} fullPath - Ruta completa del archivo
   * @returns {Promise<boolean>} - True si el archivo existe
   */
  verificarExistenciaArchivo: async (fullPath) => {
    return ejecutarConAutenticacion(async () => {
      try {
        await getMetadata(ref(storage, fullPath));
        return true;
      } catch (error) {
        if (error.code === 'storage/object-not-found') {
          return false;
        }
        throw error;
      }
    });
  },

  /**
   * Cierra la sesión de Firebase Auth
   */
  cerrarSesion: async () => {
    try {
      await signOut(auth);
      console.log('Firebase Auth - Sesión cerrada exitosamente');
    } catch (error) {
      console.error('Firebase Auth - Error cerrando sesión:', error);
      throw new Error('No se pudo cerrar la sesión');
    }
  },

  /**
   * Obtiene el usuario actualmente autenticado
   */
  obtenerUsuarioActual: () => {
    return auth.currentUser;
  },

  /**
   * Escucha cambios en el estado de autenticación
   */
  onCambioEstadoAuth: (callback) => {
    return onAuthStateChanged(auth, callback);
  }
};

export default firebaseStorageService;